import { useState, useMemo } from "react";
import { Box, HStack, Input, Spinner, Text } from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { MdSearch } from "react-icons/md";
import AppAvatar from "components/utils/AppAvatar";
import { useDashboardSocios } from "features/dashboard/socios/hooks";
import { getAvatarHexColor, resolveAvatarUrl } from "utils/avatar";

const COLS         = ["", "Nombre", "Asistencia", "Cobranza", "Estado"];
const COL_TEMPLATE = "40px 1fr 140px 130px 100px";

function mapSocioRow(row) {
  const id          = row?.socioId;
  const name        = (row?.socioNombre || "Socio sin nombre").trim();
  const activo      = row?.activo ?? true;
  const asistencia  = typeof row?.asistencia === "number" ? row.asistencia : null;
  const avatarSrc   = resolveAvatarUrl(row) || null;
  const avatarColor = getAvatarHexColor(id || name);
  return { id, name, activo, asistencia, avatarSrc, avatarColor };
}

/* ─── AttendanceBar ───────────────────────────────────────────── */

const AttendanceBar = ({ pct }) => {
  if (pct === null) return <Text fontSize="xs" color="dashboard.fg.subtle">—</Text>;
  const token = pct >= 80 ? "arawaks.jungleGreen" : pct >= 50 ? "arawaks.sunYellow" : "arawaks.campfireRed";
  return (
    <Box>
      <Text fontSize="xs" fontWeight="700" color={token} mb="1">{pct}%</Text>
      <Box h="4px" bg="dashboard.bg.surfaceMuted" borderRadius="full">
        <Box w={`${pct}%`} h="100%" bg={token} borderRadius="full" transition="width 0.4s" />
      </Box>
    </Box>
  );
};

/* ─── StatusBadge ─────────────────────────────────────────────── */

const StatusBadge = ({ activo }) => (
  <HStack
    gap="1" display="inline-flex"
    bg={{ _light: activo ? "green.50"  : "red.50",  _dark: activo ? "green.950" : "red.950"  }}
    color={{ _light: activo ? "green.700" : "red.600", _dark: activo ? "green.300" : "red.300" }}
    borderRadius="full" px="2.5" py="0.5" fontSize="xs" fontWeight="700"
  >
    <Box w="5px" h="5px" borderRadius="50%" bg={activo ? "arawaks.jungleGreen" : "arawaks.campfireRed"} flexShrink={0} />
    <Text>{activo ? "Registrado" : "Inactivo"}</Text>
  </HStack>
);

/* ─── SocioRow ────────────────────────────────────────────────── */

const SocioRow = ({ item }) => {
  const [hov, setHov] = useState(false);
  return (
    <Box
      as={Link}
      to={`/dashboard/club/socios/${item.id}`}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      display="grid" gridTemplateColumns={COL_TEMPLATE}
      alignItems="center" gap="3" px="3.5" py="2.5"
      bg={hov ? "dashboard.bg.surfaceMuted" : "dashboard.bg.surface"}
      borderRadius="12px" border="1.5px solid transparent"
      transition="all 0.15s"
      cursor="pointer"
      textDecoration="none"
    >
      <AppAvatar name={item.name} src={item.avatarSrc} size={36} color={item.avatarColor} />

      <Text fontSize="sm" fontWeight="700" color="dashboard.fg.default" lineHeight="1.2">
        {item.name}
      </Text>

      <AttendanceBar pct={item.asistencia} />

      {/* Cobranza — empty placeholder */}
      <Text fontSize="xs" color="dashboard.fg.subtle">—</Text>

      <StatusBadge activo={item.activo} />
    </Box>
  );
};

/* ─── main ────────────────────────────────────────────────────── */

const SociosCards = () => {
  const q = useDashboardSocios();

  const [search,  setSearch]  = useState("");
  const [statusF, setStatusF] = useState("todos");

  const items = useMemo(() => {
    const rows = Array.isArray(q.data) ? q.data : [];
    return rows.map(mapSocioRow).filter((i) => i.id);
  }, [q.data]);

  const filtered = useMemo(() => items.filter((s) => {
    if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusF === "registrado" && !s.activo) return false;
    if (statusF === "inactivo"   &&  s.activo) return false;
    return true;
  }), [items, search, statusF]);

  if (q.isError) {
    return (
      <pre style={{ whiteSpace: "pre-wrap", color: "crimson" }}>
        {String(q.error?.message || q.error)}
      </pre>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap="3" minH="0" flex="1" overflow="hidden">

      {/* Filter bar */}
      <HStack gap="2.5" flexWrap="wrap" flexShrink={0}>
        {/* Search */}
        <Box position="relative" maxW="280px" flex={1}>
          <Box
            position="absolute" left="10px" top="50%" transform="translateY(-50%)"
            color="dashboard.fg.subtle" pointerEvents="none" fontSize="md"
          >
            <MdSearch />
          </Box>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar socio…"
            pl="8" fontSize="sm" fontWeight="500"
            bg="dashboard.bg.surface"
            border="1.5px solid" borderColor="dashboard.border.default" borderRadius="10px"
            _focus={{ borderColor: "arawaks.campfireRed", outline: "none" }}
          />
        </Box>

        {/* Status tabs */}
        <HStack
          gap="0.5" bg="dashboard.bg.surface" borderRadius="10px" p="0.5"
          border="1px solid" borderColor="dashboard.border.default"
        >
          {[{ v: "todos", l: "Todos" }, { v: "registrado", l: "Registrados" }, { v: "inactivo", l: "Inactivos" }].map(({ v, l }) => (
            <Box
              key={v} as="button"
              onClick={() => setStatusF(v)}
              bg={statusF === v ? "arawaks.campfireRed" : "transparent"}
              color={statusF === v ? "white" : "dashboard.fg.muted"}
              border="none" borderRadius="8px" px="3.5" py="1.5"
              fontSize="sm" fontWeight="700" cursor="pointer" transition="all 0.15s"
              fontFamily="body"
            >{l}</Box>
          ))}
        </HStack>

      </HStack>

      {/* Table header */}
      <Box display="grid" gridTemplateColumns={COL_TEMPLATE} gap="3" px="3.5" flexShrink={0}>
        {COLS.map((h) => (
          <Text key={h} fontSize="10px" fontWeight="800" color="dashboard.fg.subtle"
            letterSpacing="0.06em" textTransform="uppercase">{h}</Text>
        ))}
      </Box>

      {/* Rows */}
      <Box flex="1" overflowY="auto" display="flex" flexDirection="column" gap="1">
        {q.isLoading ? (
          <Box py="10" display="flex" justifyContent="center"><Spinner /></Box>
        ) : filtered.length === 0 ? (
          <Box py="12" display="flex" alignItems="center" justifyContent="center">
            <Text fontSize="md" fontWeight="600" color="dashboard.fg.subtle">
              No hay socios registrados
            </Text>
          </Box>
        ) : filtered.map((item) => (
          <SocioRow key={item.id} item={item} />
        ))}
      </Box>

      {/* Count */}
      <Text fontSize="xs" color="dashboard.fg.subtle" fontWeight="500" flexShrink={0}>
        {filtered.length} socio{filtered.length !== 1 ? "s" : ""}
      </Text>
    </Box>
  );
};

export default SociosCards;
